import { BufferControllerEvents } from '@bufferController/BufferController.events';
import { ELifecycleError, ELifecycleEvent } from '@customTypes/events';
import { TEventBus } from '@eventBus/EventBus';
import { Fragment } from '@fragment/Fragment';
import { BasePlaylistRotationControllerEvents } from '@streamLoader/BasePlaylistRotationController/BasePlaylistRotationController.events';
import { FragmentLoaderEvents } from '@streamLoader/FragmentLoader/FragmentLoader.events';
import bind from '@utils/decorators/bind';
import { NetworkError } from '@utils/errors/Errors';

import { LifecycleEventsManagerEvents } from './LifecycleEventsManager.events';

export class LifecycleEventsManager {
  private _manifestLoaded: boolean = false;
  private _firstMediaPlaylistLoaded: boolean = false;
  private _firstFragmentLoaded: boolean = false;
  private _firstFragmentBuffered: boolean = false;

  private readonly _eventBus: TEventBus;
  private readonly _onManifestLoadedListener: string;
  private readonly _onSimplePlaylistDetectedListener: string;
  private readonly _onLLPlaylistDetectedListener: string;
  private readonly _onVODPlaylistDetectedListener: string;
  private readonly _onFragmentLoadedListener: string;
  private readonly _onFragmentBufferedListener: string;
  private readonly _onFragmentLoadErrorListener: string;
  private readonly _onFragmentLoadAbortListener: string;
  private readonly _onPlaylistLoadErrorListener: string;

  constructor(eventBus: TEventBus) {
    this._eventBus = eventBus;

    this._onManifestLoadedListener = this._eventBus.subscribe(
      BasePlaylistRotationControllerEvents.playlistType.multivariantDetected,
      this._onManifestLoaded,
    );

    this._onSimplePlaylistDetectedListener = this._eventBus.subscribe(
      BasePlaylistRotationControllerEvents.playlistType.simpleDetected,
      this._onPlaylistLoaded,
    );

    this._onLLPlaylistDetectedListener = this._eventBus.subscribe(
      BasePlaylistRotationControllerEvents.playlistType.llDetected,
      this._onPlaylistLoaded,
    );

    this._onVODPlaylistDetectedListener = this._eventBus.subscribe(
      BasePlaylistRotationControllerEvents.playlistType.vodDetected,
      this._onPlaylistLoaded,
    );

    this._onFragmentLoadedListener = this._eventBus.subscribe(FragmentLoaderEvents.fragments.fragmentLoaded, this._onFragmentLoaded);

    this._onFragmentBufferedListener = this._eventBus.subscribe(BufferControllerEvents.fragmentAdded, this._onFragmentBuffered);
    this._onFragmentLoadErrorListener = this._eventBus.subscribe(
      FragmentLoaderEvents.fragments.fragmentLoadingError,
      this._onFragmentLoadError,
    );
    this._onFragmentLoadAbortListener = this._eventBus.subscribe(
      FragmentLoaderEvents.fragments.fragmentLoadingAborted,
      this._onFragmentLoadError,
    );
    this._onPlaylistLoadErrorListener = this._eventBus.subscribe(
      BasePlaylistRotationControllerEvents.playlists.playlistLoadingError,
      this._onPlaylistLoadError,
    );
  }

  public destroy(): void {
    this._eventBus.unsubscribe(this._onManifestLoadedListener);
    this._eventBus.unsubscribe(this._onSimplePlaylistDetectedListener);
    this._eventBus.unsubscribe(this._onLLPlaylistDetectedListener);
    this._eventBus.unsubscribe(this._onVODPlaylistDetectedListener);
    this._eventBus.unsubscribe(this._onFragmentLoadedListener);
    this._eventBus.unsubscribe(this._onFragmentBufferedListener);
    this._eventBus.unsubscribe(this._onFragmentLoadErrorListener);
    this._eventBus.unsubscribe(this._onFragmentLoadAbortListener);
    this._eventBus.unsubscribe(this._onPlaylistLoadErrorListener);
  }

  public reset(): void {
    this._manifestLoaded = false;
    this._firstMediaPlaylistLoaded = false;
    this._firstFragmentLoaded = false;
    this._firstFragmentBuffered = false;
  }

  @bind
  private _onManifestLoaded(): void {
    if (this._manifestLoaded) {
      return;
    }

    this._manifestLoaded = true;
    this._eventBus.publish(LifecycleEventsManagerEvents.lifecycleEvent, { eventType: ELifecycleEvent.MANIFEST_LOADED });
  }

  @bind
  private _onPlaylistLoaded(): void {
    if (this._firstMediaPlaylistLoaded) {
      return;
    }

    this._firstMediaPlaylistLoaded = true;
    this._eventBus.publish(LifecycleEventsManagerEvents.lifecycleEvent, { eventType: ELifecycleEvent.FIRST_MEDIA_PLAYLIST_LOADED });
  }

  @bind
  private _onFragmentLoaded(): void {
    if (this._firstFragmentLoaded) {
      return;
    }

    this._firstFragmentLoaded = true;
    this._eventBus.publish(LifecycleEventsManagerEvents.lifecycleEvent, { eventType: ELifecycleEvent.FIRST_FRAGMENT_LOADED });
  }

  @bind
  private _onFragmentBuffered(): void {
    if (this._firstFragmentBuffered) {
      return;
    }

    this._firstFragmentBuffered = true;
    this._eventBus.publish(LifecycleEventsManagerEvents.lifecycleEvent, { eventType: ELifecycleEvent.FIRST_FRAGMENT_BUFFERED });
  }

  @bind
  private _onFragmentLoadError(fragment: Fragment): void {
    this._eventBus.publish(LifecycleEventsManagerEvents.lifecycleError, {
      errorType: ELifecycleError.FRAGMENT_LOAD_ERROR,
      nativeError: new NetworkError(
        ` ${fragment.error?.statusText} (${fragment.error?.status})`,
        fragment.error?.status || -1,
        fragment.error?.statusText || 'No status text known',
      ),
    });
  }

  @bind
  private _onPlaylistLoadError({ error }: { error?: { status: number; statusText: string } }): void {
    this._eventBus.publish(LifecycleEventsManagerEvents.lifecycleError, {
      errorType: this._manifestLoaded ? ELifecycleError.PLAYLIST_LOAD_ERROR : ELifecycleError.MANIFEST_LOAD_ERROR,
      nativeError: new NetworkError(
        `Could not load ${this._manifestLoaded ? 'playlist' : 'manifest'}: ${error?.statusText}(${error?.status})`,
        error?.status || -1,
        error?.statusText || 'No status text known',
      ),
    });
  }
}
